import { colorToCss } from "../color/color"
import { FieldViewModel, isFieldViewModel } from "../fields"
import { ILayerViewModel } from "../layers/types"
import { isMapLayerZone } from "../layers/utils"
import { ISoilAnalysisColor, SoilUnit } from "../soil"
import { groupBy, selectMany } from "../utils/array"
import { SoilUnitOption } from "./units"

export class SoilMapView {

    protected _colors: ISoilAnalysisColor[]

    protected _units: SoilUnitOption[]
    protected _currentUnit: string | SoilUnit

    protected _layers: ILayerViewModel[]

    protected _visible: boolean

    constructor(public name: string, public key?: string) {
    }

    public dispose() {

        if (this._layers) {
            this._layers.forEach(l => l.dispose())
        }

    }

    public getLayers(): ILayerViewModel[] {
        return this._layers || []
    }

    public showLayers(visibilityFilter?: (l: ILayerViewModel) => boolean) {

        let layers = this.getLayers()

        if (layers) {
            layers.forEach(l => this.showLayer(l, visibilityFilter))
        }

        this._visible = true

    }

    public hideLayers() {

        const { _layers: layers } = this

        if (layers) {
            layers.forEach(l => l.visible = false)
        }

        this._visible = false

    }

    protected showLayer(l: ILayerViewModel, visibilityFilter?: (l: ILayerViewModel) => boolean) {
        const visible = visibilityFilter ? visibilityFilter(l) : true
        l.visible = visible
    }

    public getColorKey() {
        return this._colors
    }

    protected setupColors(colors: ISoilAnalysisColor[]) {

        this._colors = null
        if (!colors) return

        let groups = groupBy(colors, c => c.Colour, c => colorToCss(c))

        let uniqueColors = groups.map(g => {

            let minValue = Math.min.apply(Math, g.values.map(v => v.Value))
            let minIndex = Math.min.apply(Math, g.values.map(v => v.Index))

            return {
                Colour: g.key,
                Value: minValue,
                Index: minIndex,
                Label: g.values[0].Label
            }

        })

        uniqueColors = uniqueColors.sort((c1, c2) => c1.Value - c2.Value)

        this._colors = uniqueColors

    }

    hasMultipleUnits() {
        return this.units && (this.units.length > 1)
    }

    hasSingleUnit() {
        return this.units && (this.units.length == 1)
    }

    hasUnits() {
        return !!this.units
    }

    get units() {
        return this._units
    }

    get currentUnit() {
        return this._currentUnit
    }

    set currentUnit(value: string | SoilUnit) {
        this.setCurrentUnit(value)
    }

    protected setCurrentUnit(unit: string | SoilUnit) {
        this._currentUnit = unit
    }

    protected setupUnits(units: SoilUnitOption[], current: string | SoilUnit | (() => SoilUnit)) {

        this._units = units

        // Note: for some reason, calling this.setCurrentUnit() here breaks things
        // To be reviewed later
        this._currentUnit = (current instanceof Function)
            ? current()
            : current

    }

    protected findLayers(fieldId: number, croppingId: number): ILayerViewModel[] {
        let layers = this.getLayers().filter(l => (fieldId == l.fieldId) && (croppingId == l.croppingId))
        return layers
    }


    public findFeature(data: FieldViewModel | { fieldId: number, croppingId: number, layerId: number, zoneId: number }) {

        let fieldId: number
        let croppingId: number
        let layerId = 0
        let zoneId = 0

        if (!data) return

        if (isFieldViewModel(data)) {
            fieldId = data.fieldId
            croppingId = data.croppingId
        }
        else {
            fieldId = data.fieldId
            croppingId = data.croppingId
            layerId = data.layerId
            zoneId = data.zoneId
        }

        let layers = this.findLayers(fieldId, croppingId).filter(l => !!l.mapLayer)

        let match = selectMany(

            layers.map(l => {

                let match = l.mapLayer.findFeatures(f => {

                    let data = f.get("data")

                    if (isMapLayerZone(data)) {
                        return (data.layer.datalayerId == layerId) && (data.zone.DataLayerZoneId == zoneId)
                    }

                })

                if (!(match.length || zoneId)) {
                    let features = l.mapLayer.getFeatures()
                    if (features.length == 1) match = features
                }

                return match

            }).filter(f => !!f)

        )

        return match

    }

}
